import React, { createContext, useContext, useEffect, useState } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { initializeMSAL } from '../../services/msalConfig';

interface MSALContextType {
  msalInstance: PublicClientApplication | null;
  isInitialized: boolean;
  account: AccountInfo | null;
}

const MSALContext = createContext<MSALContextType>({
  msalInstance: null,
  isInitialized: false,
  account: null,
});

export const useMSAL = () => useContext(MSALContext);

interface MSALWrapperProps {
  children: React.ReactNode;
}

export const MSALWrapper: React.FC<MSALWrapperProps> = ({ children }) => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);

  useEffect(() => {
    const initMSAL = async () => {
      try {
        const instance = await initializeMSAL();
        setMsalInstance(instance);
        
        // Check if user is already logged in
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          setAccount(accounts[0]);
        }
        
        setIsInitialized(true);
      } catch (error) {
        setIsInitialized(true); // Set to true even on error to prevent infinite loading
      }
    };

    initMSAL();
  }, []);

  const contextValue: MSALContextType = {
    msalInstance,
    isInitialized,
    account,
  };

  if (!isInitialized || !msalInstance) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        Initializing authentication...
      </div>
    );
  }

  return (
    <MSALContext.Provider value={contextValue}>
      <MsalProvider instance={msalInstance}>
        {children}
      </MsalProvider>
    </MSALContext.Provider>
  );
};